import { IQuoteLineItem, IQuoteProduct, QuoteTerm } from "../../shared/types";
import { isIsoDate, standardEndIso } from "../../shared/quoteDate";

export type QuoteDiscounts = { P1M: number; P1YM: number; P1YA: number };
export const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
const roundMargin = (value: number) => Math.round((value + Number.EPSILON) * 10000) / 10000;

export const calculateQuoteLinePricing = (preciseSale: number, preciseCost: number | null | undefined, quantity: number) => {
  const nearestCent = roundCurrency(preciseSale);
  const costFloor = preciseCost == null ? nearestCent : Math.ceil((preciseCost * 100) - Number.EPSILON) / 100;
  const sale = Math.max(nearestCent, costFloor);
  const total = roundCurrency(sale * quantity);
  const margin = preciseCost == null ? null : sale === 0 ? 0 : roundMargin((sale - preciseCost) / sale * 100);
  return { sale, total, margin };
};

// Issue 33: the effective charge window for a line, from its own override or the section dates.
// Returns null when there are no dates (the pre-Issue-33 "price a full standard term" case).
export const lineChargeWindow = (
  item: Pick<IQuoteLineItem, "term_duration" | "inherits_section_dates" | "start_date" | "end_date" | "end_date_source">,
  sectionDates: { startDate: string; endDate: string; endDateSource: "" | "manual" | "coterm" } | undefined,
): { start: string; end: string } | null => {
  const term = item.term_duration as QuoteTerm | "";
  if (!term) return null;
  const override = item.inherits_section_dates === false;
  const start = override ? item.start_date || "" : sectionDates?.startDate || "";
  if (!isIsoDate(start)) return null;
  const rawEnd = override ? item.end_date || "" : sectionDates?.endDate || "";
  const end = isIsoDate(rawEnd) ? rawEnd : standardEndIso(start, term);
  return { start, end };
};

// Issue 35: local display pricing for a line that has a customer pass-through. The promotional
// Cost (partner Cost reduced by the Microsoft percent) replaces Cost, and the pass-through
// reduces Sale additively with the discount. The server is authoritative on save; this only
// keeps the grid consistent while editing.
export const promoPricingInputs = (
  msrp: number | null | undefined,
  cost: number | null | undefined,
  discountPct: number,
  item: Pick<IQuoteLineItem, "promo_source" | "microsoft_promo_pct" | "customer_promo_pct">
) => {
  const microsoftPct = item.promo_source && item.microsoft_promo_pct ? item.microsoft_promo_pct : 0;
  const customerPct = microsoftPct > 0 ? Math.min(item.customer_promo_pct ?? 0, microsoftPct) : 0;
  const promoCost = microsoftPct > 0 && cost != null ? cost * (1 - microsoftPct / 100) : cost;
  const preciseSale = msrp == null ? null : msrp * (1 - customerPct / 100 - discountPct / 100);
  return { microsoftPct, customerPct, promoCost, preciseSale, active: microsoftPct > 0 };
};

// Issue 36: a manually priced line is full manual - its Discount is the per-line
// requested_discount_pct the user entered, never the quote-level term Discount. Sale/Total/Margin
// derive from MSRP, Cost, quantity, and that Discount (the Cost floor still applies).
// Issue 35: a manual promotion additionally lowers Cost and lets the pass-through reduce Sale.
export const calculateManualLinePricing = (item: IQuoteLineItem): IQuoteLineItem => {
  const discount = item.requested_discount_pct;
  if (item.manual_msrp_unit_price == null || discount == null || !Number.isFinite(discount)) {
    return { ...item, sale_unit_price: null, total_price: null, margin: null };
  }
  const promo = promoPricingInputs(item.manual_msrp_unit_price, item.manual_cost_unit_price, discount, item);
  const priced = calculateQuoteLinePricing(promo.preciseSale ?? 0, promo.promoCost, item.quantity);
  return { ...item, sale_unit_price: priced.sale, total_price: priced.total, margin: priced.margin };
};

// Strip the fields a line item must not send to the API. applied_discount_pct, adjustment_reason,
// and is_commission_eligible are always server-computed. An automatic line carries no priced state
// at all - the server reprices it - and must not send requested_discount_pct either. A manual line
// sends MSRP, Cost, and requested_discount_pct (the user's Discount %); the server derives
// Sale/Total/Margin. Echoing a server-computed field back is rejected by QuoteLineItemDto.Validate.
// Issue 35: promo fields to send. A partner_center line sends only promo_source +
// customer_promo_pct - the server re-resolves the id, Microsoft percent, end date and seat band
// from its stored feed. A manual line sends its full manual set. promo_eligibility*, promo_name,
// and promo_prerequisite_* are always server-computed. A line with no promotion sends nothing.
const promoRequestFields = (item: IQuoteLineItem): Partial<IQuoteLineItem> => {
  const cleared = {
    promo_id: undefined, promo_source: undefined, microsoft_promo_pct: undefined, customer_promo_pct: undefined,
    promo_end_date: undefined, promo_min_quantity: undefined, promo_max_quantity: undefined,
    promo_eligibility: undefined, promo_eligibility_detail: undefined,
    promo_name: undefined, promo_prerequisite_skus: undefined, promo_prerequisite_status: undefined,
  };
  if (item.promo_source == null) return cleared;
  if (item.promo_source === "manual") return {
    ...cleared, promo_source: "manual", customer_promo_pct: item.customer_promo_pct ?? 0,
    microsoft_promo_pct: item.microsoft_promo_pct, promo_end_date: item.promo_end_date,
    promo_min_quantity: item.promo_min_quantity ?? undefined, promo_max_quantity: item.promo_max_quantity ?? undefined,
  };
  return { ...cleared, promo_source: "partner_center", customer_promo_pct: item.customer_promo_pct ?? 0 };
};

// Issue 33: the date fields a line item may send. The charge window and proration outputs are
// server-computed and always stripped; the override snapshot is sent only for a line that
// declares its own dates (inherits_section_dates === false).
const dateRequestFields = (item: IQuoteLineItem): Partial<IQuoteLineItem> => {
  const cleared = {
    coterm_verified_at: undefined, charge_start_date: undefined, charge_end_date: undefined,
    billable_days: undefined, full_period_days: undefined, proration_detail: undefined,
  };
  if (item.inherits_section_dates === false) return {
    ...cleared, inherits_section_dates: false,
    start_date: item.start_date, end_date: item.end_date || null,
    end_date_source: item.end_date_source || null,
    coterm_target_subscription_id: item.end_date_source === "coterm" ? item.coterm_target_subscription_id ?? null : null,
    coterm_target_label: item.end_date_source === "coterm" ? item.coterm_target_label ?? null : null,
  };
  return {
    ...cleared, inherits_section_dates: true,
    start_date: undefined, end_date: undefined, end_date_source: undefined,
    coterm_target_subscription_id: undefined, coterm_target_label: undefined,
  };
};

export const toLineItemRequest = (item: IQuoteLineItem): IQuoteLineItem => {
  // Issue 50: msrp_unit_price/cost_unit_price are always computed, period-covering output for
  // both line types - never sent, on a manual line exactly as much as an automatic one.
  // manual_msrp_unit_price/manual_cost_unit_price are the raw input, sent only for a manual line.
  const base: IQuoteLineItem = {
    ...item,
    msrp_unit_price: null, cost_unit_price: null,
    sale_unit_price: null, total_price: null, margin: null,
    raw_msrp_unit_price: undefined, raw_sale_unit_price: undefined, raw_cost_unit_price: undefined,
    applied_discount_pct: undefined, adjustment_reason: undefined, is_commission_eligible: undefined,
    ...promoRequestFields(item),
    ...dateRequestFields(item),
  };
  if (item.is_price_manual) return base;
  return {
    ...base,
    requested_discount_pct: undefined,
    manual_msrp_unit_price: undefined, manual_cost_unit_price: undefined,
    is_price_unavailable: false, price_unavailable_reason: null,
  };
};

export type QuoteProductChoice = {
  product_id: string;
  sku_id: string;
  sku_title: string;
  product_title: string;
  terms: QuoteTerm[];
};

const termOrder: QuoteTerm[] = ["P1M", "P1YM", "P1YA"];
export const quoteProductTerm = (term: string, billingPlan: string): QuoteTerm | null => {
  if (term === "P1M" || term === "P1YM" || term === "P1YA") return term;
  if (term === "P1Y") return billingPlan.toLocaleLowerCase() === "annual" ? "P1YA" : "P1YM";
  return null;
};

export const groupQuoteProducts = (rows: IQuoteProduct[]): QuoteProductChoice[] => {
  const grouped = new Map<string, QuoteProductChoice>();
  rows.forEach(row => {
    const key = `${row.product_id}\u0000${row.sku_id}`;
    const current = grouped.get(key) || {
      product_id: row.product_id,
      sku_id: row.sku_id,
      sku_title: row.sku_title,
      product_title: row.product_title,
      terms: [],
    };
    const term = quoteProductTerm(row.term_duration, row.billing_plan);
    if (term && !current.terms.includes(term)) current.terms.push(term);
    grouped.set(key, current);
  });
  return Array.from(grouped.values()).map(item => ({
    ...item,
    terms: termOrder.filter(term => item.terms.includes(term)),
  }));
};

export const quoteProductsForSegment = (rows: IQuoteProduct[], segment: string) =>
  groupQuoteProducts(rows.filter(row => row.segment === segment));

// A "PRODUCT/SKU" key normalized the same way the API's QuotePromotionResolver.SkuKey is
// (upper-cased, SKU zero-padded to 4), so a line's product/SKU and a promotion's prerequisite
// bigId ("CFQ7TTC0LCHC/0002") compare equal.
export const promotionSkuKey = (productId: string, skuId: string) => {
  const sku = skuId.trim().toUpperCase();
  return `${productId.trim().toUpperCase()}/${sku.length > 0 && sku.length < 4 ? sku.padStart(4, "0") : sku}`;
};

export const promotionBigIdKey = (bigId: string) => {
  const slash = bigId.indexOf("/");
  return slash < 0 ? promotionSkuKey(bigId, "") : promotionSkuKey(bigId.slice(0, slash), bigId.slice(slash + 1));
};

export const filterQuoteProducts = (choices: QuoteProductChoice[], search: string) => {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) return choices;
  return choices.filter(item => [item.product_title, item.sku_title, item.product_id, item.sku_id]
    .some(value => value.toLocaleLowerCase().includes(needle)));
};
